import { LegacyRef, useEffect, useState, useRef } from "react";

// DEPENDENCIES
import QRCode from "qrcode";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  DocumentData,
  DocumentReference,
  Firestore,
  getFirestore,
  onSnapshot,
  setDoc,
} from "@firebase/firestore";

// UTILS
import useWindowWidth from "../../hooks/useWindowWidth";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { logAnalyticsAndHotjarEvent, persistor } from "../../main";
import { livenessStep } from "./utils/utils";
import { getLivenessSessionId } from "../../connectors/connectors";
import { closeModal, openModal } from "../../reducers/modalReducer";
import { showToastMessage } from "@almafintech/react-components/ToastMessage";
import {
  onUpdateLivenessState,
  onUpdateSessionId,
} from "../../reducers/livenessReducer";
import {
  onUpdatePrimaryButton,
  onUpdateSecondaryButton,
  onUpdateTertiaryButton,
} from "../../reducers/buttonsReducer";
import {
  onUpdateOnClose,
  onUpdateQrImage,
  onUpdateScannedQr,
  onUpdateSuccessMessage,
} from "../../reducers/qrReducer";
import { addOneValidation } from "../../reducers/validationsReducer";
import useForm from "../../hooks/useForm";
import useRemoteParams from "../../hooks/useRemoteParams";

// TYPES
import { LivenessData } from "./interfaces/types";
import {
  ComponentProps,
  ErrorResponse,
  ValidationErrorResponse,
} from "../../types";

// COMPONENTS
import Layout from "../Layout/Layout";
import Container from "../Container/Container";
import FormContainer from "../FormContainer/FormContainer";
import QRModal from "../QRModal/QRModal";
import { Player } from "@lottiefiles/react-lottie-player";
import { Button } from "@almafintech/react-components/Button";
import { InputCopy } from "@almafintech/react-components/InputCopy";
import MinimumRequirementsModal from "./MinimumRequirementsModal/MinimumRequirementsModal";
import Liveness from "./Liveness";

import styles from "./LivenessValidationCard.module.scss";
import { qrStyle } from "../../utils";

const ATTEMPTS_LIMIT = 5;

interface LivenessValidationCardProps extends ComponentProps {
  getLivenessRef: ({
    db,
    uuid,
    email,
  }: {
    db: Firestore;
    uuid: string;
    email: string;
  }) => DocumentReference<DocumentData, DocumentData>;
  onUploadLiveness: (file: File, uuid: string, cuit: string) => Promise<void>;
  showCopyLink?: boolean;
}

const LivenessValidationCard = ({
  formik,
  getLivenessRef,
  onUploadLiveness,
  showCopyLink,
}: LivenessValidationCardProps) => {
  const {
    container,
    formContainer,
    copyLinkContainer,
    externalLink,
    withQr,
    buttonsContainer,
    secondaryButton,
  } = styles;

  const { handleBack, handleContinue } = useForm(formik);

  const [error, setError] = useState<string | null>(null);

  const playerRef: LegacyRef<Player> | undefined = useRef(null);

  const dispatch = useAppDispatch();

  const { livenessState, sessionId } = useAppSelector(
    (state) => state.liveness
  );
  const { formValues } = useAppSelector((state) => state.onboardingData);
  const { icon } = useAppSelector((state) => state.activeTemplate);

  const [searchParams] = useSearchParams();
  const success = searchParams.get("success") === "true";

  // Remote
  const location = useLocation();
  const isRemoteStep = location.pathname.includes("identidad");

  const { remoteUrlParams, values } = useRemoteParams();
  const { continueWithQR, uuid, cuit, email } = values;

  const livenessLink = `${
    window.location.origin
  }/identidad?${remoteUrlParams.toString()}`;

  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [livenessData, setLivenessData] = useState<LivenessData>({
    dni: formValues?.["identityDocument"],
    gender: formValues?.["gender"],
    email,
    attemptsDesktop: 0,
    attemptsMobile: 0,
    scannedQR: false,
    isValid: false,
    faceLivenessDetection: "",
    facialRecognition: "",
  });

  // Firestore
  const db = getFirestore();
  const livenessRef = getLivenessRef({ db, uuid: uuid, email: email });

  const dimensions = useWindowWidth();
  const isMobile = dimensions.width < 768;

  const { attemptsDesktop, attemptsMobile, scannedQR, isValid } = livenessData;

  const reachedAttemptsLimit =
    (continueWithQR || isMobile ? attemptsMobile : attemptsDesktop) >=
    ATTEMPTS_LIMIT;

  const handleError = (error?: string) => {
    setError(error ?? "FAILED_VALIDATION");
    dispatch(onUpdateLivenessState("NOT_VALIDATED"));
  };

  const handleOpenQr = () => {
    dispatch(onUpdateScannedQr(false));
    dispatch(openModal({ name: "modal-qr" }));
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(livenessLink);
    showToastMessage("Link copiado al portapapeles.", {
      containerId: "layoutPage",
      type: "success",
    });
  };

  const primaryRemoteButton = {
    show: !reachedAttemptsLimit && !["VALIDATED", null].includes(livenessState),
    text:
      livenessState === "NOT_VALIDATED"
        ? "Reintentar captura"
        : "Comenzar captura",
    onClick: () => {
      setError(null);
      if (livenessState !== "NOT_VALIDATED")
        logAnalyticsAndHotjarEvent("identity_verification_started");
      dispatch(
        onUpdateLivenessState(
          livenessState === "INITIAL" ? "DETECTOR" : "INITIAL"
        )
      );
      if (livenessState === "NOT_VALIDATED") dispatch(onUpdateSessionId(""));
    },
    loading: !sessionId,
    disabled:
      error === "CAMERA_FRAMERATE_ERROR" ||
      (!!livenessState && livenessState === "DETECTOR"),
  };

  const secondaryRemoteButton = {
    show:
      !isMobile &&
      livenessState &&
      ["INITIAL", "NOT_VALIDATED", "DETECTOR"].includes(livenessState),
    text: "Validar con mi teléfono",
    onClick: handleOpenQr,
  };

  const LivenessValidation = () => (
    <Liveness
      uuid={uuid}
      sessionId={sessionId}
      cuit={cuit}
      livenessRef={livenessRef}
      onUploadLiveness={onUploadLiveness}
      isLoadingData={isLoadingData}
      livenessState={livenessState}
      livenessData={livenessData}
      reachedAttemptsLimit={reachedAttemptsLimit}
      isRemoteStep={isRemoteStep}
      continueWithQR={continueWithQR}
      isMobile={isMobile}
      error={error}
      handleError={handleError}
      playerRef={playerRef}
    />
  );

  useEffect(() => {
    if (isValid && livenessState !== "VALIDATED") {
      dispatch(closeModal());
      setDoc(livenessRef, { scannedQR: false }, { merge: true });
      logAnalyticsAndHotjarEvent("step_identity_completed_pf");
      logAnalyticsAndHotjarEvent("identity_verification_completed");

      dispatch(onUpdateLivenessState(continueWithQR ? "VALIDATED" : null));
      setTimeout(() => {
        playerRef.current?.play();
      }, 1000);
    }
  }, [isValid, livenessState]);

  useEffect(() => {
    error &&
      (error === "CAMERA_ACCESS_ERROR"
        ? dispatch(onUpdateLivenessState("CAMERA_ACCESS_REQUIRED"))
        : handleError());
  }, [error]);

  useEffect(() => {
    if (error && error !== "CAMERA_ACCESS_ERROR" && !isLoadingData) {
      const updatedAttempts = isMobile
        ? {
            attemptsMobile: attemptsMobile + 1,
          }
        : {
            attemptsDesktop: attemptsDesktop + 1,
          };
      setDoc(livenessRef, updatedAttempts, { merge: true });
    }
  }, [error, isLoadingData]);

  useEffect(() => {
    if (livenessState && !isRemoteStep) {
      if (["INITIAL", "NOT_VALIDATED"].includes(livenessState)) {
        if (!isMobile) {
          dispatch(
            onUpdateSecondaryButton({
              text: "Validar con mi teléfono",
              onClick: () => {
                setError(null);
                setDoc(livenessRef, { scannedQR: false }, { merge: true });
                handleOpenQr();
              },
              disabled: false,
              show: true,
            })
          );
        } else {
          dispatch(
            onUpdateSecondaryButton({
              text: "Volver",
              disabled: false,
              isLoading: false,
              onClick: handleBack,
              show: true,
            })
          );
          onUpdateLivenessState(null);
        }
      }

      if (isMobile) {
        dispatch(onUpdateTertiaryButton({ show: false }));
      } else {
        dispatch(
          onUpdateTertiaryButton({
            text: "Volver",
            disabled: false,
            isLoading: false,
            onClick: handleBack,
            show: true,
          })
        );
        onUpdateLivenessState(null);
      }

      switch (livenessState) {
        case "INITIAL":
          dispatch(
            onUpdatePrimaryButton({
              text: "Comenzar captura",
              disabled: false,
              isLoading: !sessionId,
              onClick: () => dispatch(onUpdateLivenessState("DETECTOR")),
              show: true,
            })
          );
          break;

        case "DETECTOR":
          dispatch(
            onUpdatePrimaryButton({
              disabled: true,
              show: true,
            })
          );
          break;

        case "NOT_VALIDATED":
        case "CAMERA_ACCESS_REQUIRED":
          dispatch(
            onUpdatePrimaryButton({
              text: "Reintentar captura",
              isLoading: false,
              onClick: () => {
                setError(null);
                dispatch(onUpdateSessionId(""));
                dispatch(onUpdateLivenessState("INITIAL"));
              },
              disabled: false,
              show: true,
            })
          );

          reachedAttemptsLimit &&
            dispatch(
              onUpdatePrimaryButton({
                disabled: true,
                show: true,
              })
            );
          break;

        case "VALIDATED":
          dispatch(
            onUpdatePrimaryButton({
              text: "Continuar",
              disabled: false,
              isLoading: false,
              type: "button",
              onClick: handleContinue,
              show: true,
            })
          );
          dispatch(
            onUpdateSecondaryButton({
              text: "Volver",
              disabled: false,
              isLoading: false,
              onClick: handleBack,
              show: true,
            })
          );
          dispatch(onUpdateTertiaryButton({ show: false }));

          onUpdateLivenessState(null);
          break;
        default:
          break;
      }
    }
  }, [livenessState, sessionId, reachedAttemptsLimit, isMobile]);

  useEffect(() => {
    livenessState === "INITIAL" &&
      (isRemoteStep || !sessionId) &&
      getLivenessSessionId()
        .then((res) => {
          const sessionId = res.data.sessionId;
          dispatch(onUpdateSessionId(sessionId));
        })
        .catch((error: ErrorResponse | ValidationErrorResponse) => {
          console.log(error)
          handleError("SESSION_ID_ERROR");
        });
    if (livenessState === "VALIDATED") {
      dispatch(
        onUpdatePrimaryButton({
          text: "Continuar",
          disabled: false,
          isLoading: false,
          type: "button",
          onClick: handleContinue,
          show: true,
        })
      );
    }
  }, [livenessState]);

  useEffect(() => {
    if (!isLoadingData && !livenessState && !isValid) {
      dispatch(
        onUpdateLivenessState(
          reachedAttemptsLimit ? "NOT_VALIDATED" : "INITIAL"
        )
      );
    }
  }, [isLoadingData, reachedAttemptsLimit, livenessState]);

  useEffect(() => {
    const unsub = onSnapshot(livenessRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data() as LivenessData;
        setLivenessData((prevState) => ({
          ...prevState,
          ...data,
        }));
        if (
          data.faceLivenessDetection !== "" &&
          data.facialRecognition !== ""
        ) {
          dispatch(
            addOneValidation({
              name: "FACE_LIVENESS_DETECTION",
              externalCode: data.faceLivenessDetection,
            })
          );
          dispatch(
            addOneValidation({
              name: "FACIAL_RECOGNITION",
              externalCode: data.facialRecognition,
            })
          );
        }
        setIsLoadingData(false);
      } else {
        setDoc(livenessRef, livenessData, { merge: true });
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (continueWithQR && !isLoadingData) {
      logAnalyticsAndHotjarEvent("qr_code_scanned_selfie");
      setDoc(livenessRef, { scannedQR: true }, { merge: true });
      localStorage.clear();
    }
  }, [continueWithQR, isLoadingData]);

  // Set QR link
  useEffect(() => {
    QRCode.toDataURL(livenessLink.concat(`&withQR=true`), qrStyle).then((img) =>
      dispatch(onUpdateQrImage(img))
    );
    dispatch(
      onUpdateSuccessMessage("Ya podés validar tu identidad desde el teléfono.")
    );
    dispatch(
      onUpdateOnClose(() =>
        setDoc(livenessRef, { scannedQR: false }, { merge: true })
      )
    );
  }, [livenessData]);

  useEffect(() => {
    dispatch(onUpdateScannedQr(scannedQR));
  }, [scannedQR]);

  useEffect(() => {
    if (isRemoteStep) {
      // Reset redux persist
      persistor.pause();
      persistor.flush().then(() => {
        return persistor.purge();
      });
      localStorage.clear();
    }
  }, [isRemoteStep]);

  return (
    <section className="h-full">
      <QRModal />
      <MinimumRequirementsModal />
      {isRemoteStep ? (
        <Layout hideStepper className={continueWithQR ? withQr : ""}>
          <Container className={container}>
            <FormContainer
              icon={icon}
              currentStep={
                livenessState === "VALIDATED" && continueWithQR
                  ? undefined
                  : livenessStep
              }
              currentSubStep={1}
              classNameFormContainer={formContainer}
              className={`${isRemoteStep ? externalLink : ""}`}
            >
              <LivenessValidation />
            </FormContainer>
          </Container>
          {!success && (
            <div
              className={`${buttonsContainer} flex gap-2 w-full justify-center`}
            >
              {secondaryRemoteButton.show && (
                <Button
                  variant="secondary"
                  text={secondaryRemoteButton.text}
                  onClick={secondaryRemoteButton.onClick}
                  className={secondaryButton}
                />
              )}
              {primaryRemoteButton.show && (
                <Button
                  text={primaryRemoteButton.text}
                  onClick={primaryRemoteButton.onClick}
                  isLoading={primaryRemoteButton.loading}
                  disabled={primaryRemoteButton.disabled}
                  fullWidth={isMobile}
                  size={isMobile ? "mobile" : "desktop"}
                />
              )}
            </div>
          )}
        </Layout>
      ) : (
        <>
          <LivenessValidation />
          {showCopyLink && livenessState === "INITIAL" && (
            <div className={copyLinkContainer}>
              <h4>
                O compartí el siguiente link y esperá a que termine la
                validación.
              </h4>
              <InputCopy value={livenessLink} onClick={handleCopyLink} />
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default LivenessValidationCard;
